// ═══════════════════════════════════════════════════════════
// Statutory Deadline Clock Engine
//
// This is the compliance core of the platform. Every deadline
// calculation flows through here — get this right.
//
// Rules:
//  - Calendar days: simple date addition
//  - Business days: skip weekends + jurisdiction holidays
//  - Extensions add days to the clock
//  - Paused days are subtracted (clock stops while paused)
//  - effectiveDeadline = submission + statutory + extension - paused
// ═══════════════════════════════════════════════════════════

export type ClockStatus = 'on-track' | 'due-soon' | 'overdue' | 'paused' | 'closed';

export interface DeadlineResult {
  effectiveDeadline: Date;
  daysRemaining: number;          // negative = overdue
  businessDaysRemaining: number;  // same but in business days
  status: ClockStatus;
  isOverdue: boolean;
  isPaused: boolean;
}

export interface ClockParams {
  dateSubmitted: Date;
  statutoryDays: number;
  businessDaysOnly: boolean;
  holidays: Set<string>;          // 'YYYY-MM-DD' strings
  extensionDays?: number;
  totalPausedDays?: number;
  clockPausedAt?: Date | null;    // non-null = clock currently paused
  isClosed?: boolean;
  dueSoonThresholdDays?: number;  // default 5 — when to go yellow
}

// ── Date helpers ─────────────────────────────────────────

function toDateStr(d: Date): string {
  return d.toISOString().split('T')[0];
}

function isWeekend(d: Date): boolean {
  const day = d.getDay();
  return day === 0 || day === 6;
}

function isHoliday(d: Date, holidays: Set<string>): boolean {
  return holidays.has(toDateStr(d));
}

function isBusinessDay(d: Date, holidays: Set<string>): boolean {
  return !isWeekend(d) && !isHoliday(d, holidays);
}

function addCalendarDays(from: Date, days: number): Date {
  const result = new Date(from);
  result.setDate(result.getDate() + days);
  return result;
}

function addBusinessDays(from: Date, days: number, holidays: Set<string>): Date {
  if (days <= 0) return new Date(from);
  let count = 0;
  const current = new Date(from);
  while (count < days) {
    current.setDate(current.getDate() + 1);
    if (isBusinessDay(current, holidays)) count++;
  }
  return current;
}

// Count business days between two dates (exclusive of start, inclusive of end)
export function countBusinessDays(from: Date, to: Date, holidays: Set<string>): number {
  if (to <= from) return 0;
  let count = 0;
  const current = new Date(from);
  current.setDate(current.getDate() + 1); // start day after `from`
  while (current <= to) {
    if (isBusinessDay(current, holidays)) count++;
    current.setDate(current.getDate() + 1);
  }
  return count;
}

// Count calendar days between two dates
export function countCalendarDays(from: Date, to: Date): number {
  const ms = to.getTime() - from.getTime();
  return Math.floor(ms / 86400000);
}

// ── Core deadline calculation ────────────────────────────

export function calculateEffectiveDeadline(params: {
  dateSubmitted: Date;
  statutoryDays: number;
  businessDaysOnly: boolean;
  holidays: Set<string>;
  extensionDays?: number;
  totalPausedDays?: number;
}): Date {
  const {
    dateSubmitted,
    statutoryDays,
    businessDaysOnly,
    holidays,
    extensionDays = 0,
    totalPausedDays = 0,
  } = params;

  const totalDays = statutoryDays + extensionDays;

  // Calculate base deadline
  const base = businessDaysOnly
    ? addBusinessDays(dateSubmitted, totalDays, holidays)
    : addCalendarDays(dateSubmitted, totalDays);

  // Add back paused days (paused time doesn't count against the clock)
  if (totalPausedDays <= 0) return base;
  return businessDaysOnly
    ? addBusinessDays(base, totalPausedDays, holidays)
    : addCalendarDays(base, totalPausedDays);
}

// ── Full clock evaluation ────────────────────────────────

export function evaluateClock(params: ClockParams): DeadlineResult {
  const {
    dateSubmitted,
    statutoryDays,
    businessDaysOnly,
    holidays,
    extensionDays = 0,
    totalPausedDays = 0,
    clockPausedAt = null,
    isClosed = false,
    dueSoonThresholdDays = 5,
  } = params;

  const effectiveDeadline = calculateEffectiveDeadline({
    dateSubmitted,
    statutoryDays,
    businessDaysOnly,
    holidays,
    extensionDays,
    totalPausedDays,
  });

  // Reference point: if clock is paused, days remaining is frozen at pause time
  const now = clockPausedAt ?? new Date();

  const daysRemaining = countCalendarDays(now, effectiveDeadline);
  const businessDaysRemaining = businessDaysOnly
    ? countBusinessDays(now, effectiveDeadline, holidays)
    : daysRemaining;

  const isOverdue = daysRemaining < 0;
  const isPaused = clockPausedAt !== null;

  let status: ClockStatus;
  if (isClosed) {
    status = 'closed';
  } else if (isPaused) {
    status = 'paused';
  } else if (isOverdue) {
    status = 'overdue';
  } else if (daysRemaining <= dueSoonThresholdDays) {
    status = 'due-soon';
  } else {
    status = 'on-track';
  }

  return { effectiveDeadline, daysRemaining, businessDaysRemaining, status, isOverdue, isPaused };
}

// ── Status display helpers ───────────────────────────────

export const CLOCK_COLORS: Record<ClockStatus, { bg: string; text: string; border: string }> = {
  'on-track':  { bg: 'var(--cds-green-50)',  text: 'var(--cds-green-700)',  border: 'var(--cds-green-200)'  },
  'due-soon':  { bg: 'var(--cds-yellow-50)', text: 'var(--cds-yellow-700)', border: 'var(--cds-yellow-200)' },
  'overdue':   { bg: 'var(--cds-red-50)',    text: 'var(--color-error)',     border: 'var(--cds-red-200)'    },
  'paused':    { bg: 'var(--cds-gray-50)', text: 'var(--color-secondary-700)', border: '#BECBD6' },
  'closed':    { bg: 'var(--cds-gray-50)', text: 'var(--cds-gray-500)', border: 'var(--cds-gray-200)' },
};

export function formatDaysRemaining(days: number, isPaused: boolean, isClosed: boolean): string {
  if (isClosed) return 'Closed';
  if (isPaused) return `Paused · ${Math.abs(days)}d remaining`;
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return 'Due today';
  if (days === 1) return '1 day left';
  return `${days} days left`;
}

// ── Acknowledgment deadline ──────────────────────────────

export function calculateAcknowledgmentDeadline(params: {
  dateSubmitted: Date;
  acknowledgmentDays: number;
  businessDaysOnly: boolean;
  holidays: Set<string>;
}): Date {
  const { dateSubmitted, acknowledgmentDays, businessDaysOnly, holidays } = params;
  return businessDaysOnly
    ? addBusinessDays(dateSubmitted, acknowledgmentDays, holidays)
    : addCalendarDays(dateSubmitted, acknowledgmentDays);
}

// ── Holiday set builder ──────────────────────────────────

export function buildHolidaySet(holidays: Array<{ date: string }>): Set<string> {
  return new Set(holidays.map(h => h.date));
}
